/**
 * @file ShopFontRow.tsx
 * @description Строка своего шрифта магазина в окне каталога (phase19.16
 * Р1-7; SB99, проект uiux §2): пометка «ваш», образец своим шрифтом, список
 * начертаний «Обычный, жирный», «· сейчас», янтарная «без русских букв ⚠»;
 * меню «⋯» 48×48 — «Добавить начертание» (выключено, когда загружены все 4),
 * «Переименовать», «Удалить». Без права настроек магазина меню нет.
 */

import { shopFontKey } from "@chaiberi/shared";
import { FilePlus2, Pencil, Trash2 } from "lucide-react";

import { Badge } from "../../ui/badge";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { FontCatalogRow } from "./FontCatalogRow";
import { FONT_SLOTS, fontStylesText, loadedSlots, type ShopFontDto } from "../../../lib/site-view/shop-fonts";

/** Пометка своего шрифта — нейтральная пилюля (и в карточке роли). */
export function OwnFontBadge() {
  return <Badge variant="default">ваш</Badge>;
}

/** Предупреждение: в шрифте нет кириллицы. */
export function NoCyrillicBadge() {
  return <Badge variant="warning">без русских букв ⚠</Badge>;
}

interface Props {
  font: ShopFontDto;
  sample: string;
  selected: boolean;
  current: boolean;
  canManage: boolean;
  onSelect: () => void;
  onAddFile: () => void;
  onRename: () => void;
  onDelete: () => void;
}

export function ShopFontRow({ font, sample, selected, current, canManage, onSelect, onAddFile, onRename, onDelete }: Props) {
  const full = loadedSlots(font).length >= FONT_SLOTS.length;
  const meta = [fontStylesText(font), current ? "сейчас" : null].filter(Boolean).join(" · ");
  return (
    <FontCatalogRow
      fontKey={shopFontKey(font.id)}
      family={font.family}
      meta={meta}
      badges={
        <>
          <OwnFontBadge />
          {font.hasCyrillic ? null : <NoCyrillicBadge />}
        </>
      }
      sample={sample}
      selected={selected}
      onSelect={onSelect}
      listItem
      menu={
        canManage ? (
          <RowActionsMenu
            title={`Шрифт «${font.family}»`}
            ariaLabel={`Действия со шрифтом «${font.family}»`}
            actions={[
              {
                key: "add",
                label: "Добавить начертание",
                hint: full ? "Все 4 начертания уже загружены" : undefined,
                icon: FilePlus2,
                disabled: full,
                run: onAddFile,
              },
              { key: "rename", label: "Переименовать", icon: Pencil, run: onRename },
              { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: onDelete },
            ]}
          />
        ) : null
      }
    />
  );
}
